import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { AnnotatedText } from "@/components/AnnotatedText";
import { toast } from "sonner";
import { ArrowLeft, Save, MessageSquarePlus, X, Highlighter } from "lucide-react";
import { SCIENCE_RUBRICS, SCIENCE_FEEDBACK_LABELS } from "@/lib/scienceRubric";
import { ScienceRubricPanel } from "@/components/ScienceRubric";
import { FeedbackPdfUpload } from "@/components/FeedbackPdf";
import { ReferenceDocs } from "@/components/ReferenceDocs";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

const CRITERIA = {
  narrative: [
    { k: "Content", d: "Ideas, imagination, development of the story and relevance to the prompt." },
    { k: "Structure", d: "Organisation, sequencing, cohesion and effective progression of the narrative." },
    { k: "Language", d: "Vocabulary, sentence structure, imagery, figurative language and overall expression." },
    { k: "Conventions", d: "Spelling, punctuation, grammar and paragraphing across the narrative." },
  ],
  persuasive: [
    { k: "Content", d: "Quality and relevance of arguments, ideas, evidence and reasoning." },
    { k: "Structure", d: "Logical organisation, introduction/conclusion, paragraphing and progression of arguments." },
    { k: "Language", d: "Persuasive techniques, vocabulary, tone, sentence variety and audience awareness." },
    { k: "Conventions", d: "Spelling, punctuation, grammar, paragraphing and formal register/connectives." },
  ],
};

export default function WritingReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [giveScore, setGiveScore] = useState(true);
  const [strengths, setStrengths] = useState("");
  const [improvements, setImprovements] = useState("");
  const [comment, setComment] = useState("");
  const [rubric, setRubric] = useState({});
  const [writingType, setWritingType] = useState("narrative");
  const [annotations, setAnnotations] = useState([]);
  const [pending, setPending] = useState(null); // {start,end,quote}
  const [pendingText, setPendingText] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [busy, setBusy] = useState(false);

  const sci = sub ? SCIENCE_RUBRICS[sub.section] : null;
  const criteria = sci ? sci.criteria : (CRITERIA[writingType] || CRITERIA.narrative);
  const total = criteria.reduce((s, c) => s + (rubric[c.k] ?? 5), 0);

  useEffect(() => {
    api.get(`/writing/${id}`).then((r) => {
      setSub(r.data);
      const f = r.data.feedback;
      if (f) {
        setStrengths(f.strengths || ""); setImprovements(f.improvements || "");
        setComment(f.comment || ""); setRubric(f.rubric || {}); setGiveScore(f.score != null || Object.keys(f.rubric || {}).length > 0);
        setAnnotations(f.annotations || []); setWritingType(f.writing_type || "narrative");
      }
    });
  }, [id]);
  if (!sub) return <Loading />;

  const addNote = () => {
    if (!pending) return;
    if (!pendingText.trim()) return toast.error("Type a note for the highlighted text.");
    setAnnotations((a) => [...a, { id: uid(), start: pending.start, end: pending.end, quote: pending.quote, comment: pendingText.trim() }]);
    setPending(null); setPendingText("");
  };
  const removeNote = (aid) => setAnnotations((a) => a.filter((x) => x.id !== aid));

  const save = async () => {
    setBusy(true);
    const full = giveScore ? Object.fromEntries(criteria.map((c) => [c.k, rubric[c.k] ?? 5])) : {};
    try {
      await api.post(`/writing/${id}/feedback`, { strengths, improvements, comment, rubric: full, annotations, writing_type: sci ? sub.section : writingType, score: giveScore ? total : null });
      toast.success("Feedback sent to student");
      navigate("/tutor/writing");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/tutor/writing")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Back</button>
      <ReferenceDocs docs={sub.reference_docs} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <div className="ember-card p-6">
            <div className="mb-1 text-xs font-semibold text-[#7C3AED]">{sub.program_name} · {sub.section_name}</div>
            <h1 className="font-heading text-2xl font-extrabold text-[#1E1B2E]">{sub.student_name}</h1>
            <div className="mt-4 rounded-xl bg-[#F5F3FF] p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-[#5B21B6]">Prompt</div>
              <p className="mt-1 text-sm text-[#1E1B2E]">{sub.prompt || (sub.reference_docs?.length ? "See the homework paper above for the writing prompt." : "No written prompt was saved for this task.")}</p>
            </div>
            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-xs font-semibold text-[#948EAB]">
                <span className="inline-flex items-center gap-1"><Highlighter className="h-3.5 w-3.5 text-[#F59E0B]" /> Select any text to add an inline note</span>
                <span>{sub.word_count} words</span>
              </div>
              <AnnotatedText text={sub.response} annotations={annotations} activeId={activeId}
                onSelectRange={(r) => { setPending(r); setPendingText(""); }} onClickAnnotation={setActiveId} />
            </div>

            {pending && (
              <div className="mt-3 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-3" data-testid="pending-note">
                <div className="mb-1.5 text-xs font-semibold text-[#B45309]">Note on: “{pending.quote.slice(0, 80)}{pending.quote.length > 80 ? "…" : ""}”</div>
                <Textarea data-testid="inline-note-input" value={pendingText} onChange={(e) => setPendingText(e.target.value)} className="min-h-[60px] rounded-lg bg-white text-sm" placeholder="Your comment on this passage…" />
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={addNote} data-testid="add-inline-note" className="h-8 rounded-lg ember-gradient text-xs font-bold"><MessageSquarePlus className="mr-1 h-3.5 w-3.5" /> Add note</Button>
                  <Button size="sm" variant="outline" onClick={() => { setPending(null); setPendingText(""); }} className="h-8 rounded-lg text-xs">Cancel</Button>
                </div>
              </div>
            )}

            {annotations.length > 0 && (
              <div className="mt-4" data-testid="inline-notes-list">
                <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#5B21B6]">Inline notes ({annotations.length})</div>
                <div className="space-y-2">
                  {annotations.map((a) => (
                    <div key={a.id} data-testid={`note-${a.id}`} onMouseEnter={() => setActiveId(a.id)} onMouseLeave={() => setActiveId(null)}
                      className="flex items-start gap-2 rounded-xl border border-[#EEEAF8] p-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-[#B45309]">“{a.quote}”</div>
                        <div className="mt-0.5 text-sm text-[#1E1B2E]">{a.comment}</div>
                      </div>
                      <button onClick={() => removeNote(a.id)} data-testid={`remove-note-${a.id}`} className="rounded-lg p-1.5 text-[#E11D48] hover:bg-[#FFF1F2]"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="ember-card p-6">
          <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Overall feedback</h2>
          <div className="mt-4 space-y-4">
            {sci && <p className="text-sm font-semibold text-[#1E1B2E]" data-testid="sci-greeting">Hi {sub.student_name?.split(" ")[0]}, thanks for the draft!</p>}
            <div><Label>{sci ? SCIENCE_FEEDBACK_LABELS.strengths : "Strengths"}</Label><Textarea data-testid="fb-strengths" value={strengths} onChange={(e) => setStrengths(e.target.value)} className="mt-1.5 rounded-xl" placeholder={sci ? "What they did especially well" : "What the student did well"} /></div>
            <div><Label>{sci ? SCIENCE_FEEDBACK_LABELS.improvements : "Areas for improvement"}</Label><Textarea data-testid="fb-improvements" value={improvements} onChange={(e) => setImprovements(e.target.value)} className="mt-1.5 rounded-xl" placeholder={sci ? "The one thing to fix first" : "What to work on next"} /></div>
            <div><Label>{sci ? SCIENCE_FEEDBACK_LABELS.comment : "Tutor comment"}</Label><Textarea data-testid="fb-comment" value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1.5 rounded-xl" placeholder={sci ? "A concrete next step" : "Overall comment"} /></div>

            {sci ? <ScienceRubricPanel rubricDef={sci} rubric={rubric} setRubric={setRubric} /> : (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Writing type & score</Label>
                <span className="rounded-full ember-gradient px-3 py-1 text-sm font-extrabold text-white" data-testid="writing-total">{total}/40</span>
              </div>
              <div className="mb-4 inline-flex rounded-xl bg-[#F5F3FF] p-1">
                {["narrative", "persuasive"].map((t) => (
                  <button key={t} type="button" data-testid={`type-${t}`} onClick={() => setWritingType(t)}
                    className={`rounded-lg px-4 py-1.5 text-sm font-bold capitalize transition-colors ${writingType === t ? "bg-white text-[#5B21B6] shadow-sm" : "text-[#948EAB]"}`}>{t}</button>
                ))}
              </div>
              <div className="space-y-4">
                {criteria.map((c) => (
                  <div key={c.k}>
                    <div className="mb-1 flex justify-between text-sm"><span className="font-semibold text-[#1E1B2E]">{c.k}</span><span className="font-bold text-[#5B21B6]">{rubric[c.k] ?? 5}/10</span></div>
                    <p className="mb-1.5 text-xs leading-snug text-[#948EAB]">{c.d}</p>
                    <Slider value={[rubric[c.k] ?? 5]} min={0} max={10} step={1} onValueChange={(v) => setRubric({ ...rubric, [c.k]: v[0] })} data-testid={`rubric-${c.k}`} />
                  </div>
                ))}
              </div>
            </div>
            )}

            <FeedbackPdfUpload kind="writing" id={sub.id} url={sub.feedback_pdf_url} onChange={(u) => setSub((p) => ({ ...p, feedback_pdf_url: u }))} />
            <label className="flex items-center gap-2 text-sm font-semibold text-[#1E1B2E]">
              <input type="checkbox" checked={giveScore} onChange={(e) => setGiveScore(e.target.checked)} data-testid="give-score-toggle" className="h-4 w-4 accent-[#4B2BB8]" />
              Include the rubric score <span className="font-normal text-[#948EAB]">(untick to send only written / PDF feedback)</span>
            </label>

            <Button onClick={save} disabled={busy} data-testid="submit-feedback" className="h-11 w-full rounded-xl ember-gradient font-bold shadow-lg shadow-violet-500/25"><Save className="mr-1.5 h-4 w-4" /> {busy ? "Saving…" : "Submit feedback"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
